import { jsPDF } from 'jspdf';

const ptToMm = (pt) => pt * 0.352778;

const truncateToWidth = (doc, text, maxWidth) => {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return '';
    if (doc.getTextWidth(trimmed) <= maxWidth) return trimmed;

    const words = trimmed.split(/\s+/).filter(Boolean);
    let low = 0;
    let high = words.length;
    let best = '';

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = words.slice(0, mid).join(' ');
        if (doc.getTextWidth(candidate) <= maxWidth) {
            best = candidate;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    if (best) return best;

    low = 0;
    high = trimmed.length;
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = trimmed.slice(0, mid).trimEnd();
        if (doc.getTextWidth(candidate) <= maxWidth) {
            best = candidate;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return best;
};

const imageUrlToDataUrl = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            } catch (err) {
                reject(err);
            }
        };
        img.onerror = reject;
        img.src = url;
    });

const normalizeToArray = (value) => {
    if (Array.isArray(value)) return value;
    return [value];
};

const renderBadgePage = async (doc, data, pageSettings) => {
    const {
        pageWidth,
        pageHeight,
        marginX,
        marginTop,
        marginBottom,
        textMaxWidth,
        lineHeightFactor,
    } = pageSettings;

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(16);

    const nickName = String(data?.nickName ?? '').trim() || '-';
    let lines = doc.splitTextToSize(nickName, textMaxWidth);
    if (lines.length > 3) {
        const firstLine = lines[0];
        const secondLine = lines[1];
        const rest = lines.slice(2).join(' ');
        lines = [
            firstLine,
            secondLine,
            truncateToWidth(doc, rest, textMaxWidth),
        ];
    }

    const fontSizePt = doc.getFontSize();
    const fontSizeMm = ptToMm(fontSizePt);
    const lineHeightMm = fontSizeMm * lineHeightFactor;
    const textY = marginTop + fontSizeMm;

    doc.text(lines, pageWidth / 2, textY, { align: 'center', lineHeightFactor });

    const textBlockBottom =
        textY + lineHeightMm * (lines.length - 1) + fontSizeMm * 0.6;
    const gapAfterText = 2;
    const minQrTop = textBlockBottom + gapAfterText;
    const availableQrHeight = pageHeight - marginBottom - minQrTop;
    const qrSize = Math.max(
        1,
        Math.min(pageWidth - marginX * 8, availableQrHeight),
    );
    const qrX = (pageWidth - qrSize) / 2;
    const qrTop = pageHeight - marginBottom - qrSize;

    const qrValue = String(data?.qrCode ?? '').trim();
    if (qrValue) {
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=0&data=${encodeURIComponent(
            qrValue,
        )}`;
        try {
            const qrDataUrl = await imageUrlToDataUrl(qrUrl);
            doc.addImage(qrDataUrl, 'PNG', qrX, qrTop, qrSize, qrSize);
        } catch (err) {}
    }
};

const PrintBadge = async (data) => {
    const previewTab = window.open('', '_blank');
    if (previewTab) {
        previewTab.document.write(
            '<div style="font-family:Arial, sans-serif; padding:16px;">Generating PDF…</div>',
        );
        previewTab.document.close();
    }

    const pageWidth = 40;
    const pageHeight = 60;
    const marginX = 2;
    const marginTop = 7;
    const marginBottom = 5;
    const textMaxWidth = pageWidth - marginX * 2;
    const lineHeightFactor = 1.15;

    const pageSettings = {
        pageWidth,
        pageHeight,
        marginX,
        marginTop,
        marginBottom,
        textMaxWidth,
        lineHeightFactor,
    };

    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight],
    });

    const items = normalizeToArray(data).filter((item) => item != null);
    const badgeItems = items.length ? items : [{}];

    for (let i = 0; i < badgeItems.length; i += 1) {
        if (i > 0) doc.addPage([pageWidth, pageHeight], 'portrait');
        await renderBadgePage(doc, badgeItems[i], pageSettings);
    }

    const pdfBlobUrl = doc.output('bloburl');
    if (!previewTab) {
        window.location.href = pdfBlobUrl;
        return;
    }
    previewTab.location.href = pdfBlobUrl;
    setTimeout(() => URL.revokeObjectURL(pdfBlobUrl), 60 * 1000);
};

export default PrintBadge;
